import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRight,
  Bike,
  Building2,
  CloudOff,
  CalendarClock,
  FileText,
  Landmark,
  ScanBarcode,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { parseAmount, ZERO_AMOUNT } from '@aeris/finance'
import { add, formatMoney } from '@aeris/money'
import {
  Badge,
  Button,
  InlineAlert,
  Page,
  PageHeader,
  Separator,
  cn,
} from '@aeris/ui'

import { MyCommissionCard } from '../components/my-commission-card.tsx'
import { ROLE_LABELS } from '../components/role-labels.ts'
import { StoreDashboard } from '../components/store-dashboard.tsx'
import { fetchProducts } from '../lib/api/catalog.ts'
import { fetchChannelOrders } from '../lib/api/channels-mercadolivre.ts'
import { fetchExchanges } from '../lib/api/exchanges.ts'
import {
  fetchAgeing,
  fetchCashSessions,
  fetchCompany,
  fetchDeliveries,
  fetchFinanceEntries,
  fetchSalesDocuments,
  fetchStockBalances,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { useBalcaoAnterior } from '../lib/balcao-anterior.ts'
import { MODULE_GROUPS } from '../lib/modules.tsx'
import { useVisibleModules } from '../lib/use-visible-modules.ts'
import { useWorkspace } from '../lib/workspace.ts'
import type { WorkspaceModule } from '../lib/workspace.ts'

/**
 * Início.
 *
 * Two jobs, in this order: say what needs attention today, and make every
 * screen in the product findable. The second is the one a home page usually
 * gets wrong — a dashboard of numbers is useless to somebody who opened the
 * product this morning and cannot find where purchases live.
 *
 * So the layout goes: what is wrong (only when something is), what happened
 * today, then every module as a labelled tile with a sentence explaining it.
 * The tiles are not decoration; they are the answer to "onde fica…?", and they
 * come from the same registry as the sidebar so the two cannot disagree about
 * what the product contains.
 *
 * Every figure here is **read from the same endpoints the screens use**. There
 * is no dashboard aggregate, no cached rollup and no invented number —
 * docs/UI_UX.md forbids fake screens, and a home page is exactly where an
 * invented figure would go unchallenged for months.
 *
 * Each panel is gated on the permission its own screen is gated on, so a
 * cashier does not learn what the shop owes by looking at the front page.
 *
 * E é isso que faz esta tela ser duas telas sem ser duas telas (F035). O balcão
 * tem dois lugares — o vendedor promete, o caixa recebe — e cada bloco aparece
 * pela CAPACIDADE de quem olha, nunca pelo nome do papel. O vendedor vê o dia
 * dele; o caixa vê a gaveta, os orçamentos esperando e as entregas a acertar; o
 * gerente vê os dois, que é o que ele precisa ver.
 */

const dateFormat = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
})

/** Midnight to midnight, local. What a shopkeeper means by "hoje". */
const today = (): { from: string; to: string } => {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const end = new Date(start.getTime() + 86_400_000 - 1)
  return { from: start.toISOString(), to: end.toISOString() }
}

/** Ontem, no relógio da loja: a comparação que o dono faz de cabeça. */
const yesterday = (): { from: string; to: string } => {
  const { from } = today()
  const start = new Date(new Date(from).getTime() - 86_400_000)
  return {
    from: start.toISOString(),
    to: new Date(new Date(from).getTime() - 1).toISOString(),
  }
}

const clock = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
})

/*
 * Os números do dia se atualizam sozinhos (F132). Quem abre o Início às 9h e
 * olha às 18h via o número das 9h — e o cartão não dizia de quando era.
 */
const EVERY_MINUTE = 60_000

const greeting = (): string => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Bom dia'
  if (hour < 18) return 'Boa tarde'
  return 'Boa noite'
}

/**
 * Everything past due, summed.
 *
 * Overdue rather than the whole balance: R$ 40.000 due next week and R$ 40.000
 * ninety days late are the same total and completely different businesses.
 *
 * Summed as fixed-point through `@aeris/money`, never with `Number(...)`:
 * ADR-0016 holds on a home page too, and adding four buckets in floating point
 * is how a total ends in `,0000000001`.
 */
const overdueOf = (
  report: { buckets: readonly { bucket: string; amount: string }[] } | undefined
): string | null =>
  report === undefined
    ? null
    : formatMoney(
        report.buckets
          .filter((bucket) => bucket.bucket !== 'current')
          .reduce(
            (running, bucket) => add(running, parseAmount(bucket.amount)),
            ZERO_AMOUNT
          )
      )

const countOverdue = (
  report: { buckets: readonly { bucket: string; count: number }[] } | undefined
): number =>
  report === undefined
    ? 0
    : report.buckets
        .filter((bucket) => bucket.bucket !== 'current')
        .reduce((running, bucket) => running + bucket.count, 0)

/**
 * A headline figure with its label underneath.
 *
 * `error` desde a F132: um cartão que não carregou mostrava "—" para sempre, e
 * um traço lê como "zero" ou "não se aplica". Agora diz que não carregou.
 */
const Stat = ({
  label,
  value,
  hint,
  Icon,
  tone = 'neutral',
  error = false,
  onOpen,
}: {
  label: string
  value: string
  hint?: string | undefined
  Icon: typeof TrendingUp
  tone?: 'neutral' | 'danger'
  error?: boolean
  /** O cartão leva à tela do número: "vendas do dia" abre Vendas. */
  onOpen?: (() => void) | undefined
}): ReactNode => {
  const Box = onOpen === undefined ? 'div' : 'button'
  return (
    <Box
      {...(onOpen === undefined
        ? {}
        : {
            type: 'button' as const,
            onClick: onOpen,
            'aria-label': `${label}: abrir`,
          })}
      className={cn(
        'flex min-w-52 flex-1 flex-col gap-1 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface) p-4 text-left',
        onOpen === undefined
          ? undefined
          : 'transition-colors hover:border-(--border-strong) hover:bg-(--surface-hover) focus-visible:outline-2 focus-visible:outline-(--border-focus)'
      )}
    >
      <span className="flex items-center gap-1.5 text-(length:--text-xs) text-(--text-muted)">
        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
        {label}
      </span>
      {error ? (
        <span className="flex items-center gap-1.5 py-1.5 text-(length:--text-sm) text-(--text-secondary)">
          <CloudOff aria-hidden="true" className="h-4 w-4" />
          Não carregou
        </span>
      ) : (
        <span
          className={cn(
            'font-(--font-mono) text-(length:--text-2xl) tabular-nums',
            tone === 'danger' ? 'text-(--danger)' : 'text-(--text-primary)'
          )}
        >
          {value}
        </span>
      )}
      {error || hint === undefined ? null : (
        <span className="text-(length:--text-xs) text-(--text-muted)">
          {hint}
        </span>
      )}
    </Box>
  )
}

/**
 * One thing that is wrong, and the button that goes to fix it.
 *
 * A warning without a way to act on it is decoration somebody learns to skip.
 */
const Attention = ({
  title,
  detail,
  action,
  onAct,
}: {
  title: string
  detail: string
  action: string
  onAct: () => void
}): ReactNode => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2">
    <div className="flex items-start gap-2">
      <AlertTriangle
        aria-hidden="true"
        className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
      />
      <div className="flex flex-col">
        <span className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
          {title}
        </span>
        <span className="text-(length:--text-xs) text-(--text-secondary)">
          {detail}
        </span>
      </div>
    </div>
    <Button variant="secondary" size="sm" onClick={onAct}>
      {action}
    </Button>
  </div>
)

const ClassicHome = (): ReactNode => {
  const { allows, session, switchContext } = useAuth()
  const classic = useBalcaoAnterior()
  const workspace = useWorkspace()

  const context = session?.context ?? null
  const day = today()
  const before = yesterday()

  const canReadSales = allows('sales.read')
  const canReadFinance = allows('finance.read')
  const canOperateTill = allows('pos.operate')
  const canReadOrganization = allows('organization.read')
  const canReadInventory = allows('inventory.read')
  const canSettle = allows('finance.settle')
  /** Quem monta orçamento e não recebe: o vendedor (ADR-0027). */
  const canSell = allows('sales.write')
  const canReadExchanges = allows('exchange.read')
  const canReadChannels = allows('channel.read')
  const canReadCatalog = allows('catalog.read')
  const quotingOnly = canSell && !canOperateTill
  const userId = session?.user.id ?? null

  const go = (module: WorkspaceModule): void => {
    workspace.open({ module })
  }

  /*
   * F360: o vendedor vê só as vendas dele (o que ele vendeu hoje, já sem as
   * canceladas), nunca o total da loja — "vendas da loja" não é sobre ele e
   * confundia com a comissão.
   */
  const mineOnly = quotingOnly && !classic
  const onlyMine =
    mineOnly && userId !== null ? { salespersonUserId: userId } : {}

  const sales = useQuery({
    queryKey: ['inicio', 'sales', day.from, onlyMine],
    queryFn: ({ signal }) =>
      fetchSalesDocuments(
        // F283: balcão + pedido entregue, a mesma conta do Painel.
        {
          sold: true,
          issuedFrom: day.from,
          issuedTo: day.to,
          pageSize: 1,
          ...onlyMine,
        },
        signal
      ),
    enabled: canReadSales,
    refetchInterval: EVERY_MINUTE,
  })

  const salesYesterday = useQuery({
    queryKey: ['inicio', 'sales', before.from, onlyMine],
    queryFn: ({ signal }) =>
      fetchSalesDocuments(
        {
          sold: true,
          issuedFrom: before.from,
          issuedTo: before.to,
          pageSize: 1,
          ...onlyMine,
        },
        signal
      ),
    enabled: canReadSales,
  })

  /* O que vence hoje, dos dois lados: a pergunta do financeiro às 9h. */
  const dueToday = useQuery({
    queryKey: ['inicio', 'due-today', day.from],
    queryFn: async ({ signal }) => {
      const due = (direction: 'receivable' | 'payable') =>
        fetchFinanceEntries(
          {
            direction,
            status: 'open',
            dueFrom: day.from,
            dueTo: day.to,
            limit: 1,
          },
          signal
        )
      const [receivableToday, payableToday] = await Promise.all([
        due('receivable'),
        due('payable'),
      ])
      return { receivableToday, payableToday }
    },
    enabled: canReadFinance,
    refetchInterval: EVERY_MINUTE,
  })

  const channelOrders = useQuery({
    queryKey: ['inicio', 'channel-orders'],
    queryFn: async ({ signal }) => {
      const [pending, failed] = await Promise.all([
        fetchChannelOrders({ status: 'pending' }, signal),
        fetchChannelOrders({ status: 'failed' }, signal),
      ])
      return { pending: pending.orders.length, failed: failed.orders.length }
    },
    enabled: canReadChannels,
    refetchInterval: EVERY_MINUTE,
  })

  const requestedExchanges = useQuery({
    queryKey: ['inicio', 'exchanges-requested'],
    queryFn: ({ signal }) =>
      fetchExchanges({ status: 'requested', pageSize: 1 }, signal),
    enabled: canReadExchanges,
  })

  const awaitingStock = useQuery({
    queryKey: ['inicio', 'awaiting-stock'],
    queryFn: ({ signal }) =>
      fetchProducts(
        { status: 'active', awaitingStock: true, pageSize: 1 },
        signal
      ),
    enabled: canReadCatalog,
  })

  const tills = useQuery({
    queryKey: ['inicio', 'tills'],
    queryFn: ({ signal }) =>
      // A loja tem poucos caixas; 100 cobre com folga e a soma não esquece o 11º.
      fetchCashSessions({ status: 'open', pageSize: 100 }, signal),
    enabled: canOperateTill,
    refetchInterval: EVERY_MINUTE,
  })

  const receivable = useQuery({
    queryKey: ['inicio', 'ageing', 'receivable'],
    queryFn: ({ signal }) => fetchAgeing('receivable', signal),
    enabled: canReadFinance,
  })

  const payable = useQuery({
    queryKey: ['inicio', 'ageing', 'payable'],
    queryFn: ({ signal }) => fetchAgeing('payable', signal),
    enabled: canReadFinance,
  })

  /**
   * O que ficou com saldo abaixo de zero (F044).
   *
   * Uma aba que ninguém abre é o mesmo que não ter tela: a regra "nenhuma
   * venda perde" produz negativo, e um negativo que ninguém vê não foi
   * reportado. Este aviso é o que faz alguém abrir a lista.
   *
   * `pageSize: 1` porque o que importa aqui é o **total** — a contagem vem no
   * envelope da página, e trazer vinte e cinco linhas para mostrar um número
   * seria pedir uma lista para contá-la.
   */
  const negatives = useQuery({
    queryKey: ['inicio', 'stock-negative'],
    queryFn: ({ signal }) =>
      fetchStockBalances({ negative: true, pageSize: 1 }, signal),
    enabled: canReadInventory,
  })

  /**
   * O dia do vendedor, medido pelo que ele mesmo assinou.
   *
   * `salespersonUserId` é o filtro que a F035 acrescentou à listagem. Sem ele
   * esta tela só saberia falar da loja inteira, que é uma informação verdadeira
   * e inútil para quem quer saber quanto ELE fez hoje.
   */
  const mine = useQuery({
    queryKey: ['inicio', 'my-quotes', day.from, userId],
    queryFn: ({ signal }) =>
      fetchSalesDocuments(
        {
          // F283: só o que ainda não virou venda — o convertido já está no vendido.
          openQuotes: true,
          salespersonUserId: userId ?? '',
          issuedFrom: day.from,
          issuedTo: day.to,
          pageSize: 1,
        },
        signal
      ),
    enabled: quotingOnly && userId !== null,
  })

  /** O que já foi prometido e ainda não foi retirado — a fila do caixa. */
  const waiting = useQuery({
    queryKey: ['inicio', 'quotes-waiting'],
    queryFn: ({ signal }) =>
      fetchSalesDocuments(
        { kind: 'quote', status: 'sent', pageSize: 1 },
        signal
      ),
    enabled: canReadSales,
  })

  /** Quanto ainda está na rua, com quem quer que seja (F035). */
  const onTheRoad = useQuery({
    queryKey: ['deliveries', 'all'],
    queryFn: ({ signal }) => fetchDeliveries({ scope: 'open' }, signal),
    enabled: canSettle,
  })

  const company = useQuery({
    queryKey: ['company', context?.companyId],
    queryFn: ({ signal }) => fetchCompany(context?.companyId ?? '', signal),
    enabled: canReadOrganization && context?.companyId != null,
  })

  /*
   * Os avisos de "Precisa de atenção" só aparecem quando há algo errado — e
   * uma consulta que falhou não é "nada errado". Sem isto, o bloco some e a
   * tela afirma em silêncio que não há título vencido nem estoque negativo.
   */
  const attentionFailed = [
    company,
    receivable,
    payable,
    negatives,
    channelOrders,
    requestedExchanges,
    awaitingStock,
  ].filter((query) => query.isError)

  const openTills = tills.data?.items ?? []
  const gaps = company.data?.company.issuanceGaps ?? []
  const lateReceivables = countOverdue(receivable.data)
  const negativeStock = negatives.data?.total ?? 0
  const latePayables = countOverdue(payable.data)
  const pendingOrders = channelOrders.data?.pending ?? 0
  const failedOrders = channelOrders.data?.failed ?? 0
  const exchangesWaiting = requestedExchanges.data?.total ?? 0
  const productsAwaiting = awaitingStock.data?.total ?? 0

  /**
   * Signed in, but standing in no account yet.
   *
   * This happens to anybody who belongs to more than one tenant: the API
   * refuses to guess, because guessing means posting a document to a company
   * nobody chose. F024 put the picker in the topbar and stopped there — so the
   * page below still rendered "Tudo o que dá para fazer" over an empty grid,
   * an empty sidebar, and no explanation. It looked like a broken account
   * rather than an unanswered question, and it was reported as one.
   *
   * So the question is asked where the eye already is.
   */
  if (context === null && (session?.memberships.length ?? 0) > 0) {
    return (
      <Page>
        <PageHeader
          eyebrow={`${greeting()}, ${session?.user.displayName ?? ''}`}
          title="Em qual conta você vai trabalhar?"
          description="Você tem acesso a mais de uma. Nada aparece até escolher — nem o menu, porque cada conta tem os seus próprios dados."
        />

        <div className="flex flex-col gap-2">
          {(session?.memberships ?? []).map((membership) => (
            <button
              key={membership.tenantId}
              type="button"
              onClick={() => {
                void switchContext({
                  tenantId: membership.tenantId,
                  companyId: null,
                  branchId: null,
                })
              }}
              className={cn(
                'flex items-center gap-3 rounded-(--radius-md) border p-4 text-left',
                'border-(--border-subtle) bg-(--surface-raised)',
                'transition-colors hover:border-(--brand-primary) hover:bg-(--surface-hover)',
                'focus-visible:outline-2 focus-visible:outline-(--border-focus)'
              )}
            >
              <Building2
                aria-hidden="true"
                className="h-5 w-5 shrink-0 text-(--text-secondary)"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-(length:--text-md) font-(--weight-medium) text-(--text-primary)">
                  {membership.tenantName}
                </span>
                <span className="block text-(length:--text-xs) text-(--text-muted)">
                  {ROLE_LABELS[membership.role]}
                </span>
              </span>
              <ArrowRight
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-(--text-muted)"
              />
            </button>
          ))}
        </div>

        <p className="text-(length:--text-xs) text-(--text-muted)">
          Dá para trocar depois a qualquer momento, pelo seletor no topo.
        </p>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        eyebrow={`${greeting()}, ${session?.user.displayName ?? ''} · ${dateFormat.format(new Date())}`}
        title={
          <span className="flex flex-wrap items-center gap-2">
            {context?.companyTradeName ?? context?.companyName ?? 'Início'}
            {context?.branchName == null ? null : (
              <Badge tone="neutral">{context.branchName}</Badge>
            )}
          </span>
        }
        actions={
          /* The one thing most people opened the product to do. */
          canSell ? (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={
                <ScanBarcode aria-hidden="true" className="h-4 w-4" />
              }
              onClick={() => {
                go('pdv')
              }}
            >
              Abrir o PDV
            </Button>
          ) : undefined
        }
      />

      {attentionFailed.length === 0 ? null : (
        <InlineAlert
          tone="warning"
          title="Não deu para conferir tudo o que precisa de atenção"
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                for (const query of attentionFailed) void query.refetch()
              }}
            >
              Tentar de novo
            </Button>
          }
        >
          Parte dos avisos não carregou. Isso não quer dizer que esteja tudo em
          dia.
        </InlineAlert>
      )}

      {/* Only rendered when there is genuinely something wrong, so its presence
          is itself information rather than furniture. */}
      {gaps.length > 0 ||
      lateReceivables > 0 ||
      latePayables > 0 ||
      negativeStock > 0 ||
      pendingOrders + failedOrders > 0 ||
      exchangesWaiting > 0 ||
      productsAwaiting > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
            Precisa de atenção
          </h2>

          {canReadOrganization && gaps.length > 0 ? (
            <Attention
              title="Faltam dados para emitir documento fiscal"
              // Names the gap rather than saying "não configurado": one is a
              // dead end, the other is a task somebody can finish today.
              detail={gaps
                .map((gap) =>
                  gap === 'state-registration'
                    ? 'inscrição estadual'
                    : gap === 'tax-regime'
                      ? 'regime tributário'
                      : gap === 'city-code'
                        ? 'código IBGE do município'
                        : 'endereço'
                )
                .join(', ')}
              action="Completar cadastro"
              onAct={() => {
                go('empresa')
              }}
            />
          ) : null}

          {lateReceivables > 0 ? (
            <Attention
              title={`${String(lateReceivables)} ${lateReceivables === 1 ? 'título vencido' : 'títulos vencidos'} a receber`}
              detail={`${overdueOf(receivable.data) ?? ''} que já passaram do vencimento.`}
              action="Ver no Financeiro"
              onAct={() => {
                go('financeiro')
              }}
            />
          ) : null}

          {negativeStock > 0 ? (
            <Attention
              title={`${String(negativeStock)} ${negativeStock === 1 ? 'produto vendido' : 'produtos vendidos'} sem estar no estoque`}
              /*
               * Diz a causa, e diz que não é urgência.
               *
               * "Saldo negativo" sozinho parece defeito do sistema e assusta
               * quem não sabe que é uma regra funcionando: a mesma peça vendida
               * no balcão e no computador da loja, e nenhuma das duas vendas
               * perde.
               */
              detail="Acontece quando a peça é vendida sem estar contada. Conte a prateleira quando passar por ela."
              action="Ver o que acertar"
              onAct={() => {
                go('estoque')
              }}
            />
          ) : null}

          {pendingOrders + failedOrders > 0 ? (
            <Attention
              title={`${String(pendingOrders + failedOrders)} ${pendingOrders + failedOrders === 1 ? 'pedido de marketplace esperando' : 'pedidos de marketplace esperando'}`}
              detail={
                failedOrders > 0
                  ? `${String(failedOrders)} com falha ao virar venda. Aceite para baixar o estoque.`
                  : 'Aceite para virar venda e baixar o estoque.'
              }
              action="Ver pedidos"
              onAct={() => {
                go('pedidos-canal')
              }}
            />
          ) : null}

          {exchangesWaiting > 0 ? (
            <Attention
              title={`${String(exchangesWaiting)} ${exchangesWaiting === 1 ? 'troca pedida' : 'trocas pedidas'}`}
              detail="O cliente está esperando o trocador aceitar ou recusar."
              action="Ver trocas"
              onAct={() => {
                go('trocas')
              }}
            />
          ) : null}

          {productsAwaiting > 0 ? (
            <Attention
              title={`${String(productsAwaiting)} ${productsAwaiting === 1 ? 'produto aguardando estoque' : 'produtos aguardando estoque'}`}
              detail="Não vendem até alguém dar entrada ou ajustar o estoque."
              action="Ver produtos"
              onAct={() => {
                go('produtos')
              }}
            />
          ) : null}

          {latePayables > 0 ? (
            <Attention
              title={`${String(latePayables)} ${latePayables === 1 ? 'conta vencida' : 'contas vencidas'} a pagar`}
              detail={`${overdueOf(payable.data) ?? ''} em atraso com fornecedores.`}
              action="Ver no Financeiro"
              onAct={() => {
                go('financeiro')
              }}
            />
          ) : null}
        </section>
      ) : null}

      {canReadSales || canOperateTill || canReadFinance ? (
        <section className="flex flex-col gap-2">
          <h2 className="flex flex-wrap items-baseline gap-2 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
            Hoje
            {sales.dataUpdatedAt === 0 ? null : (
              <span className="text-(length:--text-xs) font-(--weight-normal) text-(--text-muted)">
                atualizado às {clock.format(new Date(sales.dataUpdatedAt))}
              </span>
            )}
          </h2>
          <div className="flex flex-wrap gap-3">
            {/*
              O primeiro número é o do trabalho de quem está olhando. Para o
              vendedor, "vendas da loja" é verdadeiro e não é sobre ele.
            */}
            {quotingOnly ? (
              <Stat
                Icon={FileText}
                label="Meus orçamentos em aberto"
                error={mine.isError}
                value={
                  mine.isPending
                    ? '—'
                    : formatMoney(parseAmount(mine.data?.totalAmount ?? '0.00'))
                }
                hint={
                  mine.data === undefined
                    ? undefined
                    : mine.data.total === 1
                      ? '1 orçamento'
                      : `${String(mine.data.total)} orçamentos`
                }
              />
            ) : null}

            {canReadSales ? (
              <Stat
                Icon={TrendingUp}
                label={mineOnly ? 'Minhas vendas hoje' : 'Vendas do dia'}
                error={sales.isError}
                onOpen={() => {
                  go('vendas')
                }}
                value={
                  sales.isPending
                    ? '—'
                    : formatMoney(
                        parseAmount(sales.data?.totalAmount ?? '0.00')
                      )
                }
                hint={
                  sales.data === undefined
                    ? undefined
                    : [
                        sales.data.total === 1
                          ? '1 venda'
                          : `${String(sales.data.total)} vendas`,
                        salesYesterday.data === undefined
                          ? null
                          : `ontem ${formatMoney(parseAmount(salesYesterday.data.totalAmount))}`,
                      ]
                        .filter((part) => part !== null)
                        .join(' · ')
                }
              />
            ) : null}

            {canOperateTill ? (
              <Stat
                Icon={Wallet}
                label="Caixas abertos"
                error={tills.isError}
                onOpen={() => {
                  go('caixa')
                }}
                value={tills.isPending ? '—' : String(openTills.length)}
                hint={
                  // Enquanto carrega não há o que dizer: "nenhum caixa aberto"
                  // antes da resposta era uma afirmação falsa.
                  tills.isPending
                    ? undefined
                    : openTills.length === 0
                      ? 'nenhum caixa aberto'
                      : openTills.length === 1
                        ? `desde ${clock.format(new Date(openTills[0]?.openedAt ?? 0))}${openTills[0]?.openedByName == null ? '' : ` · ${openTills[0].openedByName}`} · esperado ${formatMoney(parseAmount(openTills[0]?.expected ?? '0.00'))}`
                        : `esperado ${formatMoney(
                            openTills.reduce(
                              (running, till) =>
                                add(running, parseAmount(till.expected)),
                              ZERO_AMOUNT
                            )
                          )}`
                }
              />
            ) : null}

            {/*
              A fila do caixa, dita para os dois lados: para o vendedor é o que
              ele prometeu e ainda não foi retirado; para o caixa é o que está
              esperando na frente dele.
            */}
            {canReadSales ? (
              <Stat
                Icon={FileText}
                label="Orçamentos aguardando"
                error={waiting.isError}
                value={
                  waiting.isPending ? '—' : String(waiting.data?.total ?? 0)
                }
                hint={
                  waiting.isPending
                    ? undefined
                    : (waiting.data?.total ?? 0) === 0
                      ? 'nada esperando no caixa'
                      : `${formatMoney(parseAmount(waiting.data?.totalAmount ?? '0.00'))} segurados`
                }
              />
            ) : null}

            {canSettle ? (
              <Stat
                Icon={Bike}
                label="Na rua, a acertar"
                error={onTheRoad.isError}
                value={
                  onTheRoad.isPending
                    ? '—'
                    : formatMoney(
                        parseAmount(onTheRoad.data?.outstandingTotal ?? '0.00')
                      )
                }
                hint={
                  onTheRoad.isPending
                    ? undefined
                    : (onTheRoad.data?.items.length ?? 0) === 0
                      ? 'nenhuma entrega em aberto'
                      : `${String(onTheRoad.data?.items.length ?? 0)} entregas`
                }
              />
            ) : null}

            {canReadFinance ? (
              <Stat
                Icon={CalendarClock}
                label="Vence hoje"
                error={dueToday.isError}
                onOpen={() => {
                  go('financeiro')
                }}
                value={
                  dueToday.data === undefined
                    ? '—'
                    : formatMoney(
                        parseAmount(
                          dueToday.data.receivableToday.outstandingTotal
                        )
                      )
                }
                hint={
                  dueToday.data === undefined
                    ? undefined
                    : `a receber · a pagar ${formatMoney(parseAmount(dueToday.data.payableToday.outstandingTotal))}`
                }
              />
            ) : null}

            {canReadFinance ? (
              <Stat
                Icon={Landmark}
                label="A receber a vencer"
                error={receivable.isError}
                value={
                  receivable.data === undefined
                    ? '—'
                    : formatMoney(
                        parseAmount(
                          receivable.data.buckets.find(
                            (bucket) => bucket.bucket === 'current'
                          )?.amount ?? '0.00'
                        )
                      )
                }
                hint="ainda dentro do prazo"
              />
            ) : null}
          </div>
        </section>
      ) : null}

      <Separator className="h-px bg-(--border-subtle)" />

      <MyCommissionCard />

      <ModuleTiles />
    </Page>
  )
}

/**
 * Todas as telas, agrupadas como o dia de trabalho — as duas versões do Início
 * terminam nelas (F134).
 */
const ModuleTiles = (): ReactNode => {
  const workspace = useWorkspace()
  const go = (module: WorkspaceModule): void => {
    workspace.open({ module })
  }

  // A mesma regra da barra lateral e da busca (F138), com as abas: a grade é
  // onde se descobre o que existe.
  const visible = useVisibleModules({ includeTabs: true }).filter(
    (entry) => entry.module !== 'inicio'
  )

  return (
    <>
      {/*
       * The findability half of the page.
       *
       * Grouped by the shape of a day's work rather than alphabetically, and
       * every tile carries a sentence: "Preços" is obvious to whoever built it
       * and not to somebody opening this on their first morning, which is
       * exactly who reads a home page.
       */}
      <section className="flex flex-col gap-4">
        <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
          Tudo o que dá para fazer
        </h2>

        {MODULE_GROUPS.map((group) => {
          const entries = visible.filter((entry) => entry.group === group)
          if (entries.length === 0) return null

          return (
            <div key={group} className="flex flex-col gap-2">
              <h3 className="text-(length:--text-xs) font-(--weight-medium) uppercase tracking-wide text-(--text-muted)">
                {group}
              </h3>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {entries.map(({ module, label, Icon, description }) => (
                  <button
                    key={module}
                    type="button"
                    onClick={() => {
                      go(module)
                    }}
                    className={cn(
                      'group flex items-start gap-3 rounded-(--radius-lg) border p-3 text-left',
                      'border-(--border-subtle) bg-(--surface)',
                      'transition-colors duration-(--duration-fast)',
                      'hover:border-(--border-strong) hover:bg-(--surface-hover)'
                    )}
                  >
                    <span className="mt-0.5 rounded-(--radius-md) bg-(--brand-primary-subtle) p-1.5">
                      <Icon
                        aria-hidden="true"
                        className="h-4 w-4 text-(--brand-primary)"
                      />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="flex items-center gap-1 text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                        {label}
                        <ArrowRight
                          aria-hidden="true"
                          className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-60"
                        />
                      </span>
                      <span className="text-(length:--text-xs) leading-(--leading-normal) text-(--text-secondary)">
                        {description}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </section>
    </>
  )
}

/**
 * O Início de quem dirige a loja (F134): o painel no lugar dos cartões do dia.
 *
 * Os avisos do cadastro fiscal continuam aqui, porque o painel fala de vender
 * e de receber — e uma nota que não sai trava as duas coisas.
 */
const OwnerHome = (): ReactNode => {
  const { allows, session } = useAuth()
  const workspace = useWorkspace()
  const context = session?.context ?? null

  const company = useQuery({
    queryKey: ['company', context?.companyId],
    queryFn: ({ signal }) => fetchCompany(context?.companyId ?? '', signal),
    enabled: allows('organization.read') && context?.companyId != null,
  })
  const gaps = company.data?.company.issuanceGaps ?? []

  return (
    <Page>
      <PageHeader
        eyebrow={`${greeting()}, ${session?.user.displayName ?? ''} · ${dateFormat.format(new Date())}`}
        title={
          <span className="flex flex-wrap items-center gap-2">
            {context?.companyTradeName ?? context?.companyName ?? 'Início'}
            {context?.branchName == null ? null : (
              <Badge tone="neutral">{context.branchName}</Badge>
            )}
          </span>
        }
        actions={
          allows('sales.write') ? (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={
                <ScanBarcode aria-hidden="true" className="h-4 w-4" />
              }
              onClick={() => {
                workspace.open({ module: 'pdv' })
              }}
            >
              Abrir o PDV
            </Button>
          ) : undefined
        }
      />

      {gaps.length > 0 ? (
        <Attention
          title="Faltam dados para emitir documento fiscal"
          detail={gaps
            .map((gap) =>
              gap === 'state-registration'
                ? 'inscrição estadual'
                : gap === 'tax-regime'
                  ? 'regime tributário'
                  : gap === 'city-code'
                    ? 'código IBGE do município'
                    : 'endereço'
            )
            .join(', ')}
          action="Completar cadastro"
          onAct={() => {
            workspace.open({ module: 'empresa' })
          }}
        />
      ) : null}

      {/*
       * O painel primeiro (F219): é a página de quem dirige. A comissão do
       * dono, quando ele tem, é o extra — vinha antes e empurrava o painel.
       */}
      <StoreDashboard />

      <MyCommissionCard />

      <Separator className="h-px bg-(--border-subtle)" />

      <ModuleTiles />
    </Page>
  )
}

/**
 * Quem dirige vê o painel; o balcão vê o dia dele (F134). A escolha é pela
 * permissão, como todo o resto desta tela — nunca pelo nome do papel.
 */
export const InicioPage = (): ReactNode => {
  const { allows } = useAuth()
  return allows('dashboard.read') ? <OwnerHome /> : <ClassicHome />
}
